import { useController } from '@framework/form/hooks/useController.ts';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useWidgetEnabled } from '../../../hooks/useWidgetEnabled.ts';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { AdvancedSelectRenderValue } from '../advanced-select-common/AdvancedSelectRenderValue.tsx';
import { AdvancedSelectWidgetComponent } from '../advanced-select-common/AdvancedSelectWidgetComponent.tsx';
import { type IWidgetAdvancedOptionValue } from '../advanced-select-common/form/createAdvancedSelectValueSchema.ts';
import { getWidgetProps } from '../common/util/getWidgetProps.ts';
import { AdvancedSingleSelectWidgetModal } from './AdvancedSingleSelectWidgetModal.tsx';
import { type IAdvancedSingleSelectWidget } from './IAdvancedSingleSelectWidget.ts';

export interface IAdvancedSingleSelectWidgetComponentProps
  extends IWidgetComponentProps<IAdvancedSingleSelectWidget> {}

/**
 * Widget per la selezione singola avanzata
 */
export function AdvancedSingleSelectWidgetComponent(
  props: IAdvancedSingleSelectWidgetComponentProps,
) {
  const { widget } = props;
  const widgetOptions = getWidgetProps(props);

  const { field } = useController(widgetOptions.name);
  const isEnabled = useWidgetEnabled(widget, widgetOptions);

  const fieldValue = useWatch<IWidgetAdvancedOptionValue>({
    name: widgetOptions.name,
  });

  const renderComponent = useMemo(() => {
    return (
      <AdvancedSelectRenderValue
        type="single"
        valueSingle={fieldValue}
        labelFieldColumns={widget.labelFieldColumns}
        columns={widget.columns}
        placeholder={widgetOptions.placeholder}
      />
    );
  }, [
    widgetOptions.placeholder,
    fieldValue,
    widget.labelFieldColumns,
    widget.columns,
  ]);

  return (
    <AdvancedSelectWidgetComponent
      {...props}
      renderComponent={renderComponent}
      renderModal={
        <AdvancedSingleSelectWidgetModal
          {...props}
          widgetOptions={widgetOptions}
          objectFieldValue={fieldValue}
          onChange={field.onChange}
          readonly={widgetOptions.readonly || !isEnabled}
        />
      }
    />
  );
}
